import { useState, useEffect, useCallback, useMemo } from "react";
import { useClientPocApi } from "../../hooks/useClientPoc";
import { useClientApi } from "../../hooks/useClientApi";
import { useCountryApi } from "../../hooks/useCountryApi"

export const useAllClientPocsViewModel = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successModal, setSuccessModal] = useState(null);

  const {
    getClientPocs,
    getClientPocById,
    createClientPoc,
    updateClientPoc:apiUpdateClientPoc,
    deleteClientPoc,
    cacheOperations: { readClientPocs, writeClientPocs },
  } = useClientPocApi();

  const {
    getCountries,
    cacheOperations: { readCountries, writeCountries },
  } = useCountryApi();

const {
    getClients,
    deleteClient,
    createClient: apiCreateClient,
    updateClient: apiUpdateClient,
    cacheOperations: { readClients, writeClients },
  } = useClientApi();

  const fetchData = useCallback(async () => {
    if (loading) return;
    setLoading(true);
    setError(null);

    try {
      const [clientPocsResult,countriesResult] = await Promise.all([getClientPocs(), getCountries()]);
      await getClients('all');
      console.log('countriesResult', countriesResult);
      
      if (!clientPocsResult.success || !countriesResult.success) {
        throw new Error("Failed to fetch required data");
      }
      writeClientPocs(clientPocsResult.data);
      writeCountries(countriesResult.data);
    } catch (err) {
      setError(err.message);
      console.error("Error fetching data:", err);
    } finally {
      setLoading(false);
    }
  }, [getClientPocs, writeClientPocs, writeCountries, getCountries]);

  useEffect(() => {
    fetchData();
  }, []);

  const handleCreateClientPoc = useCallback(
    async (formData) => {
      try {
        const result = await createClientPoc(formData);
        if (result.success) {
          setSuccessModal("Client Poc created successfully");
          return { success: true };
        } else {
          throw new Error(result.error);
        }
      } catch (error) {
        console.error("Error creating Client Poc :", error);
        throw new Error(error.message);
      }
    },
    [createClientPoc]
  );

  const handleUpdateClientPoc = useCallback(
    async (countryId, formData) => {
      try {
        const result = await apiUpdateClientPoc(countryId, formData);
        if (result.success) {
          setSuccessModal("Client Poc updated successfully");
          return { success: true };
        } else {
          throw new Error(result.error);
        }
      } catch (error) {
        console.error("Error updating Client Poc:", error);
        throw new Error(error.message);
      }
    },
    [apiUpdateClientPoc]
  );

  const handleDelete = useCallback(
    async (clientPoc) => {
      try {
        const result = await deleteClientPoc(clientPoc.client_poc_id);
        if (result.success) {
          setSuccessModal("Client Poc deleted successfully");
        } else {
          setError(result.error);
        }
      } catch (err) {
        setError(err.message);
        console.error("Error deleting Client Poc:", err);
      }
    },
    [deleteClientPoc]
  );

    const getClientName = useMemo(
      () => (id) => {
        const clients = readClients();
        const client = clients.find(
          (clnt) => clnt.client_id.toString() === id?.toString()
        );
        return client?.client_name || "Unknown";
      },
      [readClients]
    );

    const getCountryName = useMemo(
      () => (countryId) => {
        const countries = readCountries();
        const country = countries.find(
          (c) => c.country_id.toString() === countryId?.toString()
        );
        return country?.country_name || "Unknown";
      },
      [readCountries]
    );

  return {
    clientPocs: readClientPocs(),
    createClientPoc: handleCreateClientPoc,
    updateClientPoc: handleUpdateClientPoc,
    clients:readClients(),
    countries: readCountries(),
    getClientName,
    refetch:fetchData,
    handleDelete,
    successModal,
    getCountryName,
    setSuccessModal,
  };
};
